import { FC, ReactNode } from 'react';

import { MARKS as CONTENTFUL_MARKS } from '@contentful/rich-text-types';
import { Heading, Link, NodeTypes, Paragraph, ParagraphVariant } from '@purpur/library';
import type { Asset, EntryFields } from 'contentful';

import { contentfulImageUrl } from '@telialt/utils/src/Contentful/contentfulImageUrl';

import { RichTextImage, StyledRichText } from './RichTextContent.styles';
import { convertContentfulRichTextToPurpur } from './utils';

type AssetType = Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>;

type Props = {
    richText: EntryFields.RichText;
    isNegative?: boolean;
    paragraphVariant?: ParagraphVariant;
};

const MARK_TYPES = {
    [CONTENTFUL_MARKS.BOLD]: (children: ReactNode) => <strong>{children}</strong>,
    [CONTENTFUL_MARKS.ITALIC]: (children: ReactNode) => <em>{children}</em>,
    [CONTENTFUL_MARKS.UNDERLINE]: (children: ReactNode) => <u>{children}</u>,
    [CONTENTFUL_MARKS.CODE]: (children: ReactNode) => <code>{children}</code>,
    [CONTENTFUL_MARKS.STRIKETHROUGH]: (children: ReactNode) => <del>{children}</del>,
    [CONTENTFUL_MARKS.SUPERSCRIPT]: (children: ReactNode) => <sup>{children}</sup>,
    [CONTENTFUL_MARKS.SUBSCRIPT]: (children: ReactNode) => <sub>{children}</sub>,
};

export const RichTextContent: FC<Props> = ({ richText, isNegative, paragraphVariant }) => {
    if (!richText) {
        return null;
    }
    const convertedRichText = convertContentfulRichTextToPurpur(richText);

    if (!convertedRichText) {
        return null;
    }

    const customNodeTypes: NodeTypes = {
        hyperlink: ({ uri }, children) => {
            return (
                <Link variant="text" negative={isNegative}>
                    <a href={uri}>{children}</a>
                </Link>
            );
        },
        paragraph: (_, children) => <Paragraph variant={paragraphVariant || 'paragraph-100'}>{children}</Paragraph>,
        'header-2': (_, children) => (
            <Heading tag="h2" variant="title-300" negative={isNegative}>
                {children}
            </Heading>
        ),
        'header-3': (_, children) => (
            <Heading tag="h3" variant="title-200" negative={isNegative}>
                {children}
            </Heading>
        ),
        'header-4': (_, children) => (
            <Heading tag="h4" variant="title-100" negative={isNegative}>
                {children}
            </Heading>
        ),
        'header-5': (_, children) => (
            <Heading tag="h5" variant="title-100" negative={isNegative}>
                {children}
            </Heading>
        ),
        'header-6': (_, children) => (
            <Heading tag="h6" variant="title-100" negative={isNegative}>
                {children}
            </Heading>
        ),
        'embedded-asset-block': ({ data }) => {
            const asset = data?.target as AssetType;

            const fileUrl = contentfulImageUrl(asset?.fields?.file?.url);
            const altText = asset?.fields?.description || '';

            if (!fileUrl) {
                return null;
            }

            return <RichTextImage src={fileUrl} alt={altText} loading="lazy" />;
        },
        text: (node) => {
            const { value: nodeValue, marks } = node;
            const value = typeof nodeValue === 'string' ? nodeValue.replace(/\n/g, '<br />') : nodeValue;

            const element = <span dangerouslySetInnerHTML={{ __html: value as string }} />;

            if (!marks) {
                return element;
            }

            return marks.reduce((accumulator: ReactNode, currentValue: `${CONTENTFUL_MARKS}`) => {
                return MARK_TYPES[currentValue]?.(accumulator);
            }, element);
        },
    };

    return <StyledRichText data={convertedRichText} $isNegative={isNegative} customNodeTypes={customNodeTypes} />;
};
